





































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { setTimeout, clearTimeout } from 'timers';
import { IFootbowlState } from './types';
import FootbowlAction from './FootbowlAction.vue';
import FootbowlField from './FootbowlField.vue';

const MAX_REPLAY_THRESHOLD = 3; // if the number of states is less than this, we will start at beginning
const INITIAL_STATE_DISPLAY_TIME = 2.5 * 1000; // 2.5 seconds
const DISPLAY_TIME_NO_ACTION = 4 * 1000; // 4 seconds
const ACTION_DISPLAY_TIME = 5 * 1000; // 5 seconds
const FINAL_SCORE_DISPLAY_TIME = 1 * 60 * 1000; // 1 min

const stateListValidator = (obj: any) =>
  Array.isArray(obj) && obj.every(isState);

const isState = (obj: any) =>
  obj &&
  obj.sequenceNumber !== undefined &&
  obj.homeOnLeft !== undefined &&
  obj.homeScore !== undefined &&
  obj.visitorScore !== undefined &&
  obj.possession !== undefined &&
  obj.ballSide !== undefined &&
  obj.ballYardLine !== undefined &&
  obj.yardsToFirstDown !== undefined &&
  obj.tiebreaker !== undefined &&
  obj.playComplete !== undefined &&
  obj.homeTeam !== undefined &&
  obj.visitorTeam !== undefined &&
  obj.frame !== undefined &&
  obj.homePosition !== undefined &&
  obj.visitorPosition !== undefined;

@Component({
  components: {
    FootbowlAction,
    FootbowlField,
  },
})
export default class FootbowlDisplay extends Vue {
  @Prop({ required: true, type: Array, validator: stateListValidator })
  public states!: IFootbowlState[];

  @Prop({ required: false, type: Boolean })
  public replayStates!: boolean;

  private shouldDisplayAction: boolean = false;
  private displayStateLock: boolean = false;
  private displayStateIndex: number =
    this.replayStates || this.states.length <= MAX_REPLAY_THRESHOLD ? 0 : this.states.length - 1;

  private windowWidth: number = document.documentElement.clientWidth;
  private windowHeight: number = document.documentElement.clientHeight;

  private finalScoreTimer: NodeJS.Timer | null = null;

  public created() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResized);
      window.addEventListener('orientationchange', this.onResized);
    });

    if (this.replayStates) {
      setTimeout(() => this.displayNextState(), 2000);
    } else {
      // the first state gets a different display time
      this.displayStateLock = true;
      setTimeout(() => {
        this.displayStateLock = false;
        this.displayNextState();
      }, INITIAL_STATE_DISPLAY_TIME);
    }
  }

  public beforeDestroy() {
    window.removeEventListener('resize', this.onResized);
    window.removeEventListener('orientationchange', this.onResized);
  }

  get displayState() {
    return this.isDisplayingAction ?
      this.lastState :
      this.currentState;
  }

  get displayAction() {
    return this.currentState && this.isDisplayingAction
      ? this.currentState.action
      : null;
  }

  get currentState() {
    return this.states[this.displayStateIndex];
  }

  get lastState() {
    return this.states[this.displayStateIndex - 1];
  }

  get isDisplayingAction() {
    return this.shouldDisplayAction && this.currentState && !!this.currentState.action;
  }

  get isPunt() {
    const play = this.currentPlay;

    return play && play.some((s) => s.action != null &&
      (s.action.name || '').toLowerCase() === 'punt');
  }

  get isReturn() {
    const play = this.currentPlay;
    return play && play.some((s) => s.action != null &&
      ['interception', 'fumble'].indexOf((s.action.name || '').toLowerCase()) > -1);
  }

  get isFinalScore() {
    return (
      this.displayState && this.displayState.action &&
        /final_score/gi.test(this.displayState.action.name)
    );
  }

  get isFieldGoal() {
    const play = this.currentPlay;
    const curSeqNumber = (this.displayState || {}).sequenceNumber || 0;

    return play && play.some((s) =>
      s.action != null &&
      s.sequenceNumber <= curSeqNumber &&
      (s.action.name || '').toLowerCase() === 'field_goal');
  }

  get setFootbowlWidth() {
    const aspectRatio = 16 / 9;
    const windowRatio = this.windowWidth / this.windowHeight;

    if (aspectRatio > windowRatio) {
      return this.windowWidth;
    } else {
      return this.windowHeight * aspectRatio;
    }
  }

  get setFontSize() {
    const width = this.setFootbowlWidth;
    // 16px font size at 800px wide is a good starting point
    const ratio = 16 / 800;
    return width * ratio;
  }

  get currentPlay() {
    if (!this.displayState) {
      return null;
    }

    const statesByPlay = this.statesByPlay;
    for (const play in statesByPlay) {
      if (!statesByPlay.hasOwnProperty(play)) {
        continue;
      }

      if (statesByPlay[play].some((s) => s.sequenceNumber === this.displayState.sequenceNumber)) {
        return statesByPlay[play];
      }
    }

    return null;
  }

  get statesByPlay() {
    const orderedStates = this.states.slice(0).sort((a, b) => a.sequenceNumber - b.sequenceNumber);
    const statesByPlay: { [play: number]: IFootbowlState[] } = {};

    let currentPlay = 0;
    orderedStates.forEach((s) => {
      statesByPlay[currentPlay] = statesByPlay[currentPlay] || [];
      statesByPlay[currentPlay].push(s);

      if (s.playComplete) {
        currentPlay++;
      }
    });

    return statesByPlay;
  }

  @Watch('states')
  public onStatesChanged(states: IFootbowlState[], old: IFootbowlState[]) {
    if (states.length < old.length) {
      this.resetState();
      return;
    } else if (states.length === old.length) {
      // tslint:disable-next-line:no-console
      console.warn('A state was updated, they should be immutable');
      return;
    }

    this.displayNextState();
  }

  @Watch('displayState')
  public onDisplayStateChanged() {
    if (this.isFinalScore && !this.finalScoreTimer) {
      this.finalScoreTimer = setTimeout(
        () => {
          this.finalScoreTimer = null;
          this.$emit('nextGame');
        },
        FINAL_SCORE_DISPLAY_TIME,
      );
    }
  }

  private resetState() {
    this.shouldDisplayAction = false;
    this.displayStateLock = false;
    this.displayStateIndex = this.states.length - 1;
  }

  private onResized() {
    this.windowWidth = document.documentElement.clientWidth;
    this.windowHeight = document.documentElement.clientHeight;
  }

  private displayNextState() {
    const displayNextFn = () => {
      this.displayStateLock = false;
      this.displayNextState();
    };

    const hideActionFn = () => {
      this.shouldDisplayAction = false;
    };

    if (!this.displayStateLock && this.displayStateIndex < this.states.length - 1) {
      this.shouldDisplayAction = true;
      this.displayStateLock = true;
      this.displayStateIndex++;

      // Get the new action & it's display time
      const action = this.states[this.displayStateIndex].action;
      const actionWithTimeout = action && action.hasDisplayTimeout;

      // Determine the total amount of display time for this state
      let displayTime = DISPLAY_TIME_NO_ACTION;
      if (actionWithTimeout) {
        displayTime += ACTION_DISPLAY_TIME;
      } else if (action) {
        displayTime = ACTION_DISPLAY_TIME;
      }

      // Display the action or field for a minimum amount of time
      setTimeout(
        displayNextFn,
        displayTime,
      );

      // If the action has a timeout, display it first and time it out
      if (actionWithTimeout) {
        setTimeout(hideActionFn, ACTION_DISPLAY_TIME);
      }
    }
  }
}
